import { useContext, useEffect, useState } from 'react'
import { required, TextInput, useTranslate } from 'react-admin'
import { useFormContext } from 'react-hook-form'

import { Box, Button, Grid } from '@mui/material'

import { GalleryContext } from 'context/gallery/GalleryContext'
import { formStyles } from 'layout/styles/formStyles'

interface UploadFieldProps {
    source: string
    label: string
    data: any
    url: string
    validateRequired?: boolean
}

const UploadField = ({ source, label, data, url, validateRequired }: UploadFieldProps) => {
    const EMPTY_FILES_OBJ = { [source]: '' }
    const REACT_APP_MEDIA_URL = process.env.REACT_APP_MEDIA_URL

    const { state: galleryState, setGalleryState } = useContext(GalleryContext)
    const [files, setFiles] = useState({ [source]: data })
    const [currentSource, setCurrentSource] = useState('')
    const [showPreview, toggleShowPreview] = useState(false)
    const { setValue } = useFormContext()

    const translate = useTranslate()

    const formatFileName = (file: string) => {
        return file.includes('//') ? file.replace('//', '') : file.replace('/', '')
    }

    // If a file is selected from the gallery, update the local file state
    useEffect(() => {
        if (!galleryState.openGallery && galleryState.selectedFile && currentSource === source) {
            const newFileName = `${formatFileName(galleryState.selectedFile)}${
                galleryState.selectedFileExtension
            }`
            setFiles({ [source]: newFileName })
        }
    }, [currentSource, galleryState, source])

    // Update formfield data whenever local file state changes
    useEffect(() => {
        if (files) setValue(source, files[source])
    }, [setValue, files, source])

    // Open gallery
    const handleSelectFileClick = () => {
        setCurrentSource(source)
        setGalleryState({ ...galleryState, openGallery: true })
    }

    // Remove image
    const handleRemoveClick = () => {
        setFiles(EMPTY_FILES_OBJ)
    }

    return (
        <>
            <Grid
                container
                sx={{
                    ...formStyles.fullWidth
                }}
            >
                <Grid item md={5}>
                    <TextInput
                        // dirty fix to ensure path is selectable in gallery but not editable by the keyboard
                        onKeyDown={(e) => {
                            e.preventDefault()
                            return
                        }}
                        fullWidth
                        variant="outlined"
                        source={source}
                        label={label}
                        value={files[source]}
                        validate={!!validateRequired ? requiredValidate : undefined}
                    />
                </Grid>
                <Grid
                    item
                    md={5}
                    sx={{
                        ...formStyles.buttonsAfterInputContainer
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSelectFileClick}
                        sx={{
                            ...formStyles.buttonsAfterInput
                        }}
                    >
                        {translate('manager.resources.general.upload.select_file')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleRemoveClick}
                        sx={{
                            ...formStyles.buttonsAfterInput
                        }}
                    >
                        {translate('manager.resources.general.upload.remove')}
                    </Button>
                    {!!REACT_APP_MEDIA_URL && url && (
                        <Button
                            onClick={() => toggleShowPreview(!showPreview)}
                            sx={{
                                ...formStyles.buttonsAfterInput
                            }}
                        >
                            {!!showPreview
                                ? translate('manager.resources.general.upload.hide_preview')
                                : translate('manager.resources.general.upload.show_preview')}
                        </Button>
                    )}
                </Grid>
            </Grid>
            {!!showPreview && !!REACT_APP_MEDIA_URL && (
                <Box mb={2}>
                    <Box
                        component="img"
                        sx={{
                            maxWidth: '100%'
                        }}
                        src={REACT_APP_MEDIA_URL + url}
                        alt={source}
                    />
                </Box>
            )}
        </>
    )
}

const requiredValidate = [required()]

export default UploadField
